import React, { useState } from "react";
import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  ModalFooter,
} from "reactstrap";
import EditorFormSection from "../../EditorFormSection";
import fileguideIcon from "../../../assets/images/icons/fileguide_colored.svg";
import checkMarkSVG from "../../../assets/images/icons/checkmark.svg";
import { UncontrolledPopover, PopoverHeader, PopoverBody } from "reactstrap";
import { useSelector } from "react-redux";

const fileChecks = [
  {
    key: "filecheck",
    title: "Filecheck.Filecheck.Title",
    description: "Filecheck.Filecheck.Description",
    popover: "Filecheck.Filecheck.Popover",
    iconURL: "https://cdn.myfolie.de/media/products/filecheck.png",
  },
  {
    key: "productiondata",
    title: "Filecheck.ProductionData.Title",
    description: "Filecheck.ProductionData.Description",
    popover: "Filecheck.ProductionData.Popover",
    iconURL: "https://cdn.myfolie.de/media/products/productiondata.png",
  },
  {
    key: "design-service",
    title: "Filecheck.DesignService.Title",
    description: "Filecheck.DesignService.Description",
    popover: "Filecheck.DesignService.Popover",
    iconURL: "https://cdn.myfolie.de/media/products/productiondata.png",
    disabled: true,
  },
];

const FileCheckSection = ({ isOpen, togglePanel, addingToCart }, context) => {
  const { control, setValue, watch } = useFormContext();
  const { language } = useSelector((state) => ({ language: state.language }));
  const [skipFilecheck, setSkipFilecheck] = useState(false);
  const selectedFileCheck = watch("filecheck");

  return (
    <EditorFormSection
      help={context.t("FileEditor.filecheck.help")}
      panelName="filecheck"
      panelTitle={context.t("Should we check your file before printing?")}
      open={isOpen}
      toggle={togglePanel}
    >
      {fileChecks.map((fileCheck, index) => (
        <Row
          key={fileCheck.key}
          xs={12}
          className={`filecheck-field-label ${
            fileCheck.key === selectedFileCheck ? "checked" : ""
          } ${fileCheck.disabled ? "disabled" : ""}`}
          onClick={() =>
            !fileCheck.disabled && setValue("filecheck", fileCheck.key)
          }
          id={`filecheck_${index}_id`}
        >
          <Col xs={3} className="filecheck-field-icon-container">
            <img
              src={fileCheck.iconURL}
              alt={fileCheck.title}
              className="filecheck-field-icon"
              loading="lazy"
            />
            {fileCheck.key === selectedFileCheck && (
              <img
                src={checkMarkSVG}
                className="filecheck-field-checkmark"
                alt="selected filecheck"
                loading="lazy"
              />
            )}
          </Col>
          <Col xs={9} className="filecheck-field-details">
            <div className="filecheck-field-details-title">
              <strong>{context.t(fileCheck.title)}</strong>
            </div>
            <div className="filecheck-field-details-subtitle">
              <small>{context.t(fileCheck.description)}</small>
            </div>
            <span className="text-remark" id={`filecheck_${index}_id_details`}>
              {context.t("Details")}
            </span>
          </Col>

          <UncontrolledPopover
            trigger="hover"
            delay={{ show: 0, hide: 0 }}
            placement="right"
            target={`filecheck_${index}_id_details`}
          >
            <PopoverHeader>{context.t(fileCheck.title)}</PopoverHeader>
            <PopoverBody>
              <div
                dangerouslySetInnerHTML={{
                  __html: context.t(fileCheck.popover),
                }}
              />
            </PopoverBody>
          </UncontrolledPopover>
        </Row>
      ))}

      <Row>
        <div className="d-flex">
          <Input
            type="radio"
            className="filecheck-field-nofilecheck-radio"
            style={{ position: "relative" }}
            checked={"no-filecheck" === selectedFileCheck ? "checked" : ""}
            onChange={() => setValue("filecheck", "no-filecheck")}
            id="radiobutton-nofilecheck"
          />
          <Label
            className="filecheck-field-nofilecheck-label text-remark mt-3"
            htmlFor="radiobutton-nofilecheck"
          >
            {context.t("No Filecheck Description")}
          </Label>
        </div>
        <Col xs={12} className="text-center mt-3">
          <a
            href={`/${language}/fileguide/`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={fileguideIcon}
              alt={context.t("Production Data Guidelines")}
              height="18"
            />{" "}
            {context.t("Production Data Guidelines")}
          </a>
        </Col>
      </Row>

      <Modal
        fade={false}
        isOpen={selectedFileCheck === "no-filecheck" && !skipFilecheck}
      >
        <ModalHeader>{context.t("NoFilecheck.alert.title")}</ModalHeader>
        <ModalBody>
          <div
            dangerouslySetInnerHTML={{
              __html: context.t("NoFilecheck.alert.body"),
            }}
          />
          <p className="text-center mt-1">
            <a
              href={`/${language}/fileguide/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={fileguideIcon}
                alt={context.t("Production Data Guidelines")}
                height="20"
              />{" "}
              {context.t("Production Data Guidelines")}
            </a>
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => setValue("filecheck", "filecheck")}
          >
            {context.t("Please do a file check")}
          </Button>{" "}
          <Button
            color="secondary"
            onClick={() => {
              setValue("filecheck", "no-filecheck");
              setSkipFilecheck(true);
            }}
          >
            {context.t("I understand")}
          </Button>
        </ModalFooter>
      </Modal>
    </EditorFormSection>
  );
};

FileCheckSection.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  togglePanel: PropTypes.func.isRequired,
};

FileCheckSection.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default FileCheckSection;
